import { Divider, Typography } from '@material-ui/core';
import { GetApp, InsertDriveFile } from '@material-ui/icons';
import { eventoFinanceiroDescriptions, pagamentoStatusDescriptions } from 'common/genericDescriptions';
import {
  PagamentoResponse,
  ParticipanteResumo,
  Fechamento,
  InvestidorResumo,
  PagamentoDocuments,
} from 'common/genericTypes';
import { GenericDrawer, GenericDrawerTextContent } from 'components/genericDrawer';
import { Button, MdSpace, SmSpace } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { formatDate } from 'utils/date';
import { addCpfCnpjMask } from 'utils/mask';
import { formatCurrencyNumber, formatNumber } from 'utils/number';
import ErrorIcon from '@material-ui/icons/Error';

interface ConfirmarLiquidacaoDrawerProps {
    pagamentoProps?: PagamentoResponse;
	pagamentoDocuments?: Array<PagamentoDocuments>;
    opened: boolean;
    onClose(): void;
    onSubmitSuccess(reject?: boolean): void;
    reject?: boolean;
    contestar?: boolean;
}

interface FechamentoPorCustodiante {
    custodiante: ParticipanteResumo;
    fechamentos: Array<Fechamento>;
    qtdTotal: number;
    valorTotal: number;
}

const ConfirmarLiquidacaoDrawerComponent: React.FC<ConfirmarLiquidacaoDrawerProps> = (props) => {
  const { pagamentoProps, pagamentoDocuments, opened, onClose, reject, onSubmitSuccess, contestar } = props;
  const [pagamento, setPagamento] = useState<PagamentoResponse>();
  const [valorMonetarioTotal, setValorMonetarioTotal] = useState<number>(0);
  const [fechamentosPorCustodiante, setFechamentosPorCustodiante] = useState<Array<FechamentoPorCustodiante>>([]);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const {
    getPagamento: getPagamentoApi,
    postConfirmarLiquidacaoPagamento,
    postCancelarLiquidacaoPagamento,
    postContestarLiquidacaoPagamento
  } = useSelector((state: RootState) => state.instrumentoApi);

  const handleConfirmarLiquidacao = async () => {
    if (pagamento) {
      try {
        if (reject) {
          await postCancelarLiquidacaoPagamento(dispatch, pagamento.id, true);
          onSubmitSuccess(reject);
        } else if (contestar) {
          await postContestarLiquidacaoPagamento(dispatch, pagamento.id, true);
          onSubmitSuccess(reject);
        } else if (!pagamento.blocked) {
          await postConfirmarLiquidacaoPagamento(dispatch, pagamento.id, true);
          onSubmitSuccess(reject);
        }
      } catch (error) {}
    }
  };

    const getPagamento = useCallback(async () => {
        setLoading(true)
        if (pagamentoProps !== undefined) {
            try {
                const result = await getPagamentoApi(dispatch, pagamentoProps?.id.toString(), true);
                setPagamento(result);
                if (result.fechamentos?.length > 0) {
                    let fechamentos: Array<FechamentoPorCustodiante> = [];
                    let valorTotalAtivos: number = 0;
                    const custodiantesUnicos = Array.from(new Set(result.fechamentos.map((f) => f.custodiante.id)))
                        .map((id) => result.fechamentos.find((_) => _.custodiante.id === id)?.custodiante)
                        .filter((_) => _ !== undefined)
                        .map((_) => _ as ParticipanteResumo);

                    const investidoresUnicos = Array.from(new Set(result.fechamentos.map((f) => f.investidor.id)))
                        .map((id) => result.fechamentos.find((_) => _.investidor.id === id)?.investidor)
                        .filter((_) => _ !== undefined)
                        .map((_) => _ as InvestidorResumo);

                    for (let i = 0; i < custodiantesUnicos.length; i++) {
                        const filtrados = result.fechamentos.filter((_) => _.custodiante.id === custodiantesUnicos[i].id);
                        const valorTotal = filtrados.map((_) => _.quantidade * _.precoUnitario).reduce((a, b) => a + b, 0);
                        let qtdTotal = 0;

                        for (let j = 0; j < investidoresUnicos.length; j++) {
                            qtdTotal += filtrados.find((_) => _.investidor.id === investidoresUnicos[j].id)?.quantidade ?? 0;
                        }

                        fechamentos.push({
                            custodiante: custodiantesUnicos[i],
                            fechamentos: filtrados,
                            valorTotal,
                            qtdTotal,
                        });

                        valorTotalAtivos += valorTotal;
                    }
                    setFechamentosPorCustodiante(fechamentos);
                    setValorMonetarioTotal(valorTotalAtivos);
                }
            } catch (error) { }
        }
        setLoading(false)
    }, [dispatch, getPagamentoApi, pagamentoProps]);

  const textContents: GenericDrawerTextContent[] = [
    {
      text: `Tem certeza que deseja ${reject ? 'cancelar'  : contestar ? 'Contestar' : 'confirmar'} a liquidação do instrumento financeiro ${
        pagamento?.instrumentoFinanceiro.codigo
      } ?`,
    },
    {
      subtitle: 'Data de Vencimento',
      text: pagamento?.dataVencimento ? formatDate(pagamento.dataVencimento) : '-',
    },
    {
      subtitle: 'Status',
      text: pagamento?.status ? pagamentoStatusDescriptions[pagamento.status] : '-',
    },
    {
      subtitle: 'Tipo',
      text: pagamento?.status ? eventoFinanceiroDescriptions[pagamento.tipo] : '-',
    },
    {
      subtitle: 'Total de ativos',
      text: (pagamento?.quantidade || pagamento?.instrumentoFinanceiro.quantidadeDepositada) ? formatNumber(pagamento.quantidade ?? pagamento.instrumentoFinanceiro.quantidadeDepositada, { minimumFractionDigits: 0, maximumFractionDigits: 8 }) : "-",
    },
    {
      subtitle: 'Valor Total de Pagamento',
      text: formatCurrencyNumber(valorMonetarioTotal),
    },
    ...fechamentosPorCustodiante.flatMap((pfc) => {
      return [
        {
          header: 'Custodiante',
          space: true,
        },
        {
          subtitle: 'CPF/CNPJ',
          text: addCpfCnpjMask(pfc.custodiante.cnpj ?? ''),
        },
        {
          subtitle: 'Razão Social',
          text: pfc.custodiante.razaoSocial,
        },
        {
          subtitle: 'Quantidade Total',
          text: formatNumber(pfc.qtdTotal, { minimumFractionDigits: 0, maximumFractionDigits: 8 })
        },
        {
          subtitle: 'Valor Total',
          text: formatNumber(pfc.valorTotal, { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 8 }),
          space: true,
        },
        {
          header: 'Investidores',
          space: true,
        },
        ...pfc.fechamentos.flatMap((fechamento) => {
          return [
            {
              subtitle: 'CPF/CNPJ',
              text: addCpfCnpjMask(fechamento.investidor.cpfCnpj),
            },
            {
              subtitle: 'Razão Social',
              text: fechamento.investidor.razaoSocial,
            },
            {
              subtitle: 'Tipo',
              text: eventoFinanceiroDescriptions[fechamento.tipo],
            },
            {
              subtitle: 'Quantidade',
              text: formatNumber(fechamento.quantidade, { minimumFractionDigits: 0, maximumFractionDigits: 8 })
            },
            {
              subtitle: 'Valor Total',
              text: formatNumber(fechamento.quantidade * fechamento.precoUnitario, { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 8 }),
              space: true,
            },
          ];
        }),
      ];
    }),
  ];

    useEffect(() => {
        getPagamento();
    }, [getPagamento]);

	const getAttachmentLink = async (doc: PagamentoDocuments) => {
		try {
		if (doc) {
			const link = document.createElement('a');
			link.href = doc.url;
			link.target = '_blank';
			link.download = doc.fileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
		} catch (error) {
		console.log(error);
		}
	};

	function shortName(name: string, extension: string) {
		if (name.length > 20) return `${name.slice(0, 20)}...${extension}`;

		return `${name}${extension}`;
	}

  const comprovantes = () => {
    const lista = pagamentoDocuments?.map((document, i) => (
      <div key={i}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: '1px solid #C4C4C4',
            paddingBottom: 40,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', alignContent: 'flex-end', textAlign: 'right' }}>
            <InsertDriveFile style={{ marginRight: 10 }} fontSize={'small'} />
            <div style={{ maxWidth: 300, display: 'flex' }}>
              <Typography
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                variant="body1"
              >
                {shortName(document.fileName, document.extension)}
              </Typography>
            </div>
            <Typography style={{ marginLeft: 10 }}>{`-`}</Typography>
            <Typography style={{ marginLeft: 10 }}>{`${
              Math.round((document.size / 1000000) * 100) / 100
            }MB`}</Typography>
            <GetApp style={{ marginLeft: 20 }} />
            <Button onClick={() => getAttachmentLink(document)} variant="text">
              {'Download'}
            </Button>
          </div>
        </div>
        <MdSpace />
      </div>
    ));

    return (
      <>
        <SmSpace />
        <Typography variant="h3">{`Comprovantes`}</Typography>
        <Divider variant="fullWidth" />
        <SmSpace />
        {pagamento?.hasDocuments ? (
          lista
        ) : (
          <>
            <Typography variant="body2">Evento financeiro não possui comprovantes.</Typography>
            <SmSpace />
          </>
        )}
      </>
    );
  };

  const pagamentoBloqueado = () => {
    if (pagamento?.blocked)
        return (
            <div
            style={{
                marginBottom: '20px',
                padding: '20px',
                border: '2px solid #FFCF40',
                display: 'flex',
                alignItems: 'center',
            }}
            >
                <ErrorIcon style={{ marginRight: 10 }} fontSize="small" />
                <Typography variant="body2">
                    {` Não é possível confirmar a liquidação pois existe pagamento(s) de JUROS pendente(s)!`}
                </Typography>
                <SmSpace />
            </div>
      );
  };

  return (
    <GenericDrawer
      opened={opened}
      title={`${reject ? 'Cancelar' : contestar ? 'Contestar' : 'Confirmar'} Solicitação de Liquidação`}
      textContents={!loading ? textContents : []}
      onClose={onClose}
      buttons={[
        {
          buttonText: `${reject ? 'Cancelar' : contestar ? 'Contestar' : 'Confirmar'} Liquidação`,
          buttonAction: handleConfirmarLiquidacao,
          buttonColor: reject ? 'error' : 'primary',
          buttonDisable: reject || contestar ? false : pagamento?.blocked ?? false,
        },
        {
          buttonText: 'Cancelar',
          buttonAction: onClose,
        },
      ]}
    >
      {comprovantes()}
      {pagamentoBloqueado()}
    </GenericDrawer>
  );
};

export const ConfirmarLiquidacaoDrawer = ConfirmarLiquidacaoDrawerComponent;
